import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import LoginPage from './pages/LoginPage';
import BookingWizard from './pages/BookingWizard';
import SpaceTypeSelector from './pages/SpaceTypeSelector';
import MeetingRoomBooking from './pages/MeetingRoomBooking';
import AdminPage from './pages/AdminPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminKiosks from './pages/AdminKiosks';
import AdminDeskAllocation from './pages/AdminDeskAllocation';
import UnitAdminPage from './pages/UnitAdminPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ConfirmArrival from './pages/ConfirmArrival';
import ProfilePage from './pages/ProfilePage';
import ManageBookingsPage from './pages/ManageBookingsPage';
import BookingHistoryPage from './pages/BookingHistoryPage';
import ChatAssistant from './components/ChatAssistant';
import MaintenanceBubble from './components/MaintenanceBubble';
import './App.css';

// Session timeout duration: 15 minutes in milliseconds
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

function App() {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(() => {
        try {
            const storedUser = localStorage.getItem('user');
            return storedUser && storedUser !== 'undefined' ? JSON.parse(storedUser) : null;
        } catch (e) {
            console.error('Failed to parse user from localStorage', e);
            return null;
        }
    });
    const [loading, setLoading] = useState(true);

    // Initialize from localStorage if available, otherwise now
    const lastActivityRef = useRef(
        parseInt(localStorage.getItem('lastActivity')) || Date.now()
    );

    // Logout handler - MUST be defined before useEffects that use it
    const handleLogout = useCallback(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivity');
        delete axios.defaults.headers.common['Authorization'];
        window.location.href = '/login';
    }, []);

    // Setup Setup Axios Interceptor for 401s (Global)
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response && error.response.status === 401) {
                    // Only logout if we actually have a token to avoid loops
                    if (localStorage.getItem('token')) {
                        console.log('Global 401 interceptor caught error, logging out');
                        // Use the stable handleLogout reference? 
                        // We can't easily use handleLogout here without adding it to deps, 
                        // which might cause re-registration of interceptor on every render if handleLogout changes (it shouldn't).
                        // Instead, we can manually do the cleanup here or dispatch an event.
                        // Ideally, we just call the function.

                        // To allow calling handleLogout without circular deps or re-running:
                        // We can direct manipulate localStorage and window.location here as a failsafe
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        localStorage.removeItem('lastActivity');
                        delete axios.defaults.headers.common['Authorization'];
                        window.location.href = '/login';
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    useEffect(() => {
        const initializeAuth = async () => {
            if (token) {
                // Change 1: Check inactivity immediately on load
                const lastActivity = parseInt(localStorage.getItem('lastActivity')) || Date.now();
                if (Date.now() - lastActivity > SESSION_TIMEOUT_MS) {
                    console.log('Session timed out (persisted)');
                    handleLogout();
                    setLoading(false);
                    return;
                }

                try {
                    const decoded = JSON.parse(atob(token.split('.')[1]));
                    let resolvedUser = decoded;

                    if (decoded && !decoded.unit) {
                        try {
                            const storedUser = localStorage.getItem('user');
                            if (storedUser && storedUser !== 'undefined') {
                                const parsedUser = JSON.parse(storedUser);
                                resolvedUser = { ...decoded, ...parsedUser };
                            }
                        } catch (e) {
                            console.warn('Error parsing stored user details', e);
                        }
                    }

                    // Check if token is expired
                    if (decoded.exp && decoded.exp < Date.now() / 1000) {
                        console.log('Token expired');
                        throw new Error('Token expired');
                    }

                    // Set authorization header BEFORE making API calls
                    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    console.log('Authorization header set for user:', resolvedUser.email);

                    // Fetch authoritative user record from server
                    try {
                        const meRes = await axios.get('/api/auth/me');
                        const authoritative = meRes.data || resolvedUser;
                        resolvedUser = { ...resolvedUser, ...authoritative };
                    } catch (err) {
                        console.log('Failed to fetch /api/auth/me, using cached user data');
                    }

                    // Check if this user is unit-admin
                    try {
                        console.log('Checking if user is unit admin...');
                        const res = await axios.get('/api/check-unit-admin');
                        if (res?.data) {
                            resolvedUser = {
                                ...resolvedUser,
                                ...res.data
                            };
                            if (res.data.isUnitAdmin) {
                                resolvedUser.is_unit_admin = true;
                                resolvedUser.unit_admin_unit = res.data.unitName;
                            }
                        }
                    } catch (e) {
                        // ignore
                    }

                    // Update state with fully resolved user
                    setUser(resolvedUser);
                    localStorage.setItem('user', JSON.stringify(resolvedUser));
                } catch {
                    setToken(null);
                    setUser(null);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('lastActivity');
                    delete axios.defaults.headers.common['Authorization'];
                }
            } else {
                setUser(null);
                delete axios.defaults.headers.common['Authorization'];
            }
            setLoading(false);
        };

        initializeAuth();
    }, [token, handleLogout]);


    // Session timeout: 15 minutes of inactivity
    useEffect(() => {
        if (!token) return; // No need to track inactivity if not logged in

        const checkInactivity = () => {
            const now = Date.now();
            // Read from localStorage to sync tabs or get persisted time
            const lastActivity = parseInt(localStorage.getItem('lastActivity')) || lastActivityRef.current;

            if (now - lastActivity > SESSION_TIMEOUT_MS) {
                console.log('Session timed out due to inactivity');
                handleLogout();
            }
        };

        // --- HEARTBEAT LOGIC ---
        // Send heartbeat to server every 5 minutes if user is active
        const sendHeartbeat = async () => {
            // Only send if logged in
            if (!localStorage.getItem('token')) return;

            try {
                await axios.get('/api/auth/heartbeat');
                console.log('Heartbeat sent');
            } catch (err) {
                console.error('Heartbeat failed', err);
                if (err.response && err.response.status === 401) {
                    // If server says unauthorized (timed out), logout
                    handleLogout();
                }
            }
        };

        const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
        let lastHeartbeatTime = Date.now();

        const updateActivity = () => {
            const now = Date.now();
            lastActivityRef.current = now;
            localStorage.setItem('lastActivity', now.toString());

            // If it's been > 5 mins since last heartbeat, send one now
            if (now - lastHeartbeatTime > HEARTBEAT_INTERVAL) {
                lastHeartbeatTime = now;
                sendHeartbeat();
            }
        };

        // Throttle updateActivity to run at most once every 5 seconds
        let throttleTimer;
        const throttledUpdateActivity = () => {
            if (!throttleTimer) {
                throttleTimer = setTimeout(() => {
                    updateActivity();
                    throttleTimer = null;
                }, 5000);
            }
        };

        // Check inactivity every minute
        const intervalId = setInterval(checkInactivity, 60000);

        const events = ['mousemove', 'keydown', 'scroll', 'click'];
        events.forEach(event => {
            window.addEventListener(event, throttledUpdateActivity);
        });

        return () => {
            clearInterval(intervalId);
            if (throttleTimer) clearTimeout(throttleTimer);
            events.forEach(event => {
                window.removeEventListener(event, throttledUpdateActivity);
            });
        };
    }, [token, handleLogout]);

    const handleLogin = (newToken, userData) => {
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        // Init activity
        const now = Date.now();
        localStorage.setItem('lastActivity', now.toString());
        lastActivityRef.current = now;

        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    };

    const handleUserUpdate = (updatedUser) => {
        if (!updatedUser) {
            return;
        }
        const mergedUser = { ...user, ...updatedUser };
        setUser(mergedUser);
        localStorage.setItem('user', JSON.stringify(mergedUser));
    };

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <Router>
            <Routes>
                <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                    path="/dashboard"
                    element={token ? <Navigate to="/book" /> : <Navigate to="/login" />}
                />
                <Route
                    path="/book"
                    element={token ? <SpaceTypeSelector user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/booking"
                    element={token ? <BookingWizard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/meeting-room-booking"
                    element={token ? <MeetingRoomBooking user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/profile"
                    element={token ? <ProfilePage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/bookings/manage"
                    element={token ? <ManageBookingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/bookings/history"
                    element={token ? <BookingHistoryPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/admin"
                    element={token && user?.is_admin ? <AdminPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/admin/dashboard"
                    element={token && user?.is_admin ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/admin/desk-allocation"
                    element={token && user?.is_admin ? <AdminDeskAllocation user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/admin/kiosks"
                    element={token && user?.is_admin ? <AdminKiosks user={user} /> : <Navigate to="/login" />}
                />
                <Route
                    path="/unit-admin"
                    element={token && (user?.is_admin || user?.is_unit_admin) ? <UnitAdminPage user={user} /> : <Navigate to="/login" />}
                />
                <Route path="/" element={token ? <Navigate to="/book" /> : <Navigate to="/login" />} />
                <Route path="/confirm-arrival" element={<ConfirmArrival />} />
            </Routes>

            {/* AI Chat Assistant - only show when logged in */}
            {token && <ChatAssistant user={user} />}
            <MaintenanceBubble />
        </Router>
    );
}

export default App;
